// ============================================================
// SupportModal.tsx
// In-app "Support" popup opened from the sidebar footer. Lets a
// logged-in user fire a short message to the Casevo team without
// leaving the app or opening a mail client. Submits directly to
// Web3Forms (no backend, no schema) which relays the message to
// hello@casevo.co.uk and uses the user's email as reply-to.
//
// Modal chrome (overlay + dialog refs, onMouseDown backdrop close,
// window-level Escape close + Tab focus trap, rAF-deferred auto-
// focus on the textarea, restore-focus on close, reset-on-open) is
// ported verbatim from AddActivityModal / ClientDocumentUploadModal
// so this feels identical to every other Casevo modal.
//
// Minimal by design — ONE required textarea. The sender's name,
// email, and firm are NOT typed by the user: they're pulled from
// the authenticated user in useCasevoStore and attached to the
// payload automatically (see handleSubmit). firmName is omitted
// entirely — AuthUser only carries firmId, no human-readable firm
// name, so per spec we drop the field rather than send an id.
//
// Frontend-only. No API module, no backend route, no DB write.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { useCasevoStore } from '../../store/useCasevoStore'

// Web3Forms public access key. Safe to ship in client code — it's a
// write-only submission key scoped to the hello@casevo.co.uk inbox,
// not a secret. This is exactly how Web3Forms is designed to be used
// from a static/SPA frontend.
const WEB3FORMS_ACCESS_KEY = 'fa9482f9-a2f1-43bd-a8cb-c9214c20d682'
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit'

interface SupportModalProps {
  isOpen: boolean
  onClose: () => void
}

// Submission lifecycle — mirrors ClientDocumentUploadModal's
// UploadMode union. 'idle' shows the form; 'sending' disables the
// Send button and relabels it "Sending…"; 'success' swaps the body
// for a confirmation; 'error' keeps the form (with what the user
// typed intact) and surfaces the app's inline error pattern.
type SendMode = 'idle' | 'sending' | 'success' | 'error'

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // The authenticated user — the single source of the name/email we
  // auto-attach to the payload. Read as a live selector so a re-login
  // as a different user always sends the correct identity.
  const authUser = useCasevoStore((s) => s.authUser)

  // Form + submission state.
  const [message, setMessage] = useState<string>('')
  const [mode, setMode] = useState<SendMode>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Honeypot. A hidden field real users never see or fill; bots that
  // blindly complete every input trip it and Web3Forms drops the
  // submission. Bound to state so we can send it in the JSON payload.
  const [botcheck, setBotcheck] = useState<string>('')
  // Unsaved-changes guard: a message typed but not sent — ×, Esc, backdrop,
  // Cancel (and leaving the screen / Back / refresh) ask first.
  const guardClose = useUnsavedChangesGuard(isOpen && message.trim() !== '' && (mode === 'idle' || mode === 'error'))
  const requestClose = (): void => guardClose(onClose)

  // Reset all state on open + capture the previously-focused element
  // for restore-on-close. Same shape as AddActivityModal's reset
  // effect. The parent keeps this component MOUNTED across isOpen
  // toggles, so we clear explicitly rather than relying on unmount.
  useEffect(() => {
    if (isOpen) {
      setMessage('')
      setMode('idle')
      setErrorMessage(null)
      setBotcheck('')
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
    }
    return () => {
      if (isOpen) {
        previouslyFocusedRef.current?.focus?.()
      }
    }
  }, [isOpen])

  // Auto-focus the textarea on open. rAF-deferred so the DOM is
  // committed before we ask for focus.
  useEffect(() => {
    if (!isOpen) return
    const id = requestAnimationFrame(() => {
      textareaRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [isOpen])

  // Escape close + Tab focus trap. Single window-level listener;
  // freshest dialog content read via dialogRef at keydown time.
  // Escape is suppressed mid-send so we don't leave a request in
  // flight against a closed modal.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mode === 'sending') return
        e.preventDefault()
        guardClose(onClose)
        return
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => el.offsetParent !== null)
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose, mode, guardClose])

  // Backdrop close — onMouseDown so a click that starts inside the
  // dialog and drags onto the backdrop doesn't close. Suppressed
  // mid-send.
  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mode === 'sending') return
    if (e.target !== overlayRef.current) return
    guardClose(onClose)
  }

  // ── SUBMIT ───────────────────────────────────────────────────
  // Explicit empty-check validation (NOT native `required`) — the
  // codebase convention is a plain onClick button with no <form>
  // onSubmit (see AddActivityModal.handleSubmit's `if
  // (trimmedAction.length === 0)` guard), so native validation
  // wouldn't fire. We block an empty submit here, before the fetch.
  const handleSubmit = async (): Promise<void> => {
    if (mode === 'sending') return
    setErrorMessage(null)
    const trimmed = message.trim()
    if (trimmed.length === 0) {
      setErrorMessage('Please tell us how we can help before sending.')
      setMode('error')
      return
    }
    // Auto-attach the sender's identity from the store — the user
    // never types these. name is "First Last"; email doubles as the
    // Web3Forms reply-to so we can respond to them directly. firm is
    // omitted: AuthUser carries only firmId (no display name), and
    // the spec says omit the field when a firm name isn't available.
    const senderName = authUser
      ? `${authUser.firstName} ${authUser.lastName}`.trim()
      : ''
    const payload: Record<string, string> = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'Casevo support request',
      name: senderName,
      email: authUser?.email ?? '',
      message: trimmed,
      botcheck, // empty for real users; bots that fill it are dropped
    }
    setMode('sending')
    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => null)
      if (res.ok && data && data.success === true) {
        setMessage('') // clear the textarea on success, per spec
        setMode('success')
      } else {
        // Non-success response — preserve what they typed so they
        // don't lose it, and show the app's inline error pattern.
        setErrorMessage(
          (data && typeof data.message === 'string' && data.message) ||
            "Something went wrong sending your message. Please try again.",
        )
        setMode('error')
      }
    } catch {
      // Network failure — same treatment: keep the message, show the
      // error banner.
      setErrorMessage(
        "We couldn't reach our servers. Check your connection and try again.",
      )
      setMode('error')
    }
  }

  if (!isOpen) return null

  const isSending = mode === 'sending'

  return (
    <div
      ref={overlayRef}
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4 py-6"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-modal-title"
        className="bg-casevo-surface rounded-lg border border-casevo-border w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        {/* Header — fixed; flex-shrink-0 keeps it pinned when the body
            scrolls under it. */}
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-casevo-border flex-shrink-0">
          <h3
            id="support-modal-title"
            className="text-sm font-medium text-casevo-forest uppercase tracking-widest"
          >
            Support
          </h3>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="text-casevo-mid hover:text-casevo-forest text-lg leading-none px-2 -mr-2"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body — flex-1 overflow-y-auto so content on a
            small viewport scrolls under the fixed header. */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {mode === 'success' ? (
              // ── SUCCESS STATE ───────────────────────────────────
              // Form body is replaced by a confirmation. The user
              // closes the modal themselves (Close button / X / Esc /
              // backdrop). Textarea was already cleared on success.
              <>
                <p className="text-sm text-casevo-forest leading-relaxed">
                  Thanks — we'll get back to you by email.
                </p>
                <div className="flex pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Helper text — one line under the heading. */}
                <p className="text-sm text-casevo-mid leading-relaxed">
                  Tell us what's up and we'll get back to you by email.
                </p>

                {/* ── Message (required) ───────────────────────────
                    The single input. `required` is kept as an a11y /
                    belt-and-braces hint, but the load-bearing guard is
                    the explicit empty-check in handleSubmit (the
                    codebase uses onClick buttons, not <form> onSubmit,
                    so native validation wouldn't fire). */}
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-casevo-mid mb-1 block">
                    How can we help?
                  </span>
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isSending}
                    required
                    rows={5}
                    placeholder="Describe the issue or question…"
                    className="w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid resize-y leading-relaxed disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </label>

                {/* Honeypot — visually hidden from real users; only a
                    bot fills it. Not `type="hidden"` because bots skip
                    those; off-screen text input is the standard
                    Web3Forms honeypot pattern. */}
                <input
                  type="text"
                  name="botcheck"
                  value={botcheck}
                  onChange={(e) => setBotcheck(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />

                {/* Inline error — same pattern as AddActivityModal /
                    ClientDocumentUploadModal. What the user typed is
                    preserved above, so they can just retry. */}
                {mode === 'error' && errorMessage && (
                  <p className="text-sm text-casevo-danger">{errorMessage}</p>
                )}

                {/* Footer — equal-width Cancel + Send pair, mirroring
                    the app's other modals. Send disables + relabels
                    "Sending…" while in flight. */}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={isSending}
                    className="flex-1 border border-casevo-border text-casevo-forest text-sm py-2 rounded hover:border-casevo-mid transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => { void handleSubmit() }}
                    disabled={isSending}
                    className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupportModal
